import { useCallback, useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import API_ENDPOINTS from '../../../apiEndpoints';
import { useAsync } from '../../../hooks/useAsync';
import { isValidEmail } from '../../../utils/Regex';
import { logIn } from '../../../redux/reducers/login';
import { persistor } from '../../../redux/store';
import { closeSnackbar, showErrorSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';
import { checkAccountVerified } from '../api/checkAccount.api';
import {
  createLoginSession,
  getActiveCognitoSessions,
  getCognitoUserSessionsList,
  globalSignOut,
  logoutCognitoSessions,
  signInWithGoogle,
} from '../api/login.api';
import type { LoginFormValues } from '../schemas/loginSchema';
import { ForgotPasswordFieldError, type ForgotPasswordResult } from '../types/forgotPassword';
import { getCurrentAccessToken } from '../utils/auth.utils';

type ForceLogoutAndLoginParams = LoginFormValues & {
  accessToken: string;
};

export function useAuth() {

  useEffect(() => {
    closeSnackbar();
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showActiveSessionModal, setShowActiveSessionModal] = useState(false);

  const completeLogin = useCallback(
    async (deviceKey: string) => {
      const { sessionId } = await createLoginSession({ LinkedSessionID: deviceKey });
      localStorage.setItem('sessionID', String(sessionId));
      dispatch(logIn());
      navigate('/');
    },
    [dispatch, navigate]
  );

  const login = useAsync(async (values: LoginFormValues) => {
    try {
      await Auth.signIn(values.email, values.password);
      const sessions = await getCognitoUserSessionsList();
      const activeSessions = getActiveCognitoSessions(sessions);

      const deviceKey = Date.now().toString();
      localStorage.setItem('deviceKey', deviceKey);

      if (activeSessions.length) {
        const accessToken = await getCurrentAccessToken();
        if (accessToken) {
          setShowActiveSessionModal(true);
          return;
        }
      }

      await completeLogin(deviceKey);
      localStorage.setItem('Remembered', 'false');
      document.cookie = 'rememberMe=false; path=/; secure; samesite=strict';
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };

      if (err.code === 'UserNotConfirmedException') {
        showErrorSnackbar(err.message?.length ? err.message : 'Network Error');
        try {
          await Auth.resendSignUp(values.email);
          navigate('/confirmsignup', { state: { email: values.email, password: values.password } });
        } catch {
          showErrorSnackbar('Too many Attempts! Please try again later');
        }
      } else if (err.code === 'UserNotFoundException') {
        const checkResponse = await axios.post(API_ENDPOINTS.REACT_APP_SIGNUP_CHECK, { email: values.email });
        showWarningSnackbar(checkResponse.data.error);
      } else if (err.code === 'NotAuthorizedException') {
        showErrorSnackbar(err.message ?? 'Not authorized');
      } else {
        showErrorSnackbar(err.message ?? 'Network Error');
      }
      throw error;
    }
  }, false);

  const googleSignIn = useAsync(async () => {
    try {
      await signInWithGoogle();
    } catch {
      showErrorSnackbar('Unable to sign in with Google');
      return;
    }
  }, false);

  const forgotPassword = useAsync(
    async (email: string): Promise<ForgotPasswordResult> => {
      const trimmedEmail = email.trim();

      if (!trimmedEmail) {
        throw new ForgotPasswordFieldError('Please enter an email');
      }

      if (!isValidEmail(trimmedEmail)) {
        throw new ForgotPasswordFieldError('Invalid email address');
      }

      try {
        const { userExists, isUnconfirmed, isGoogleAccount } = await checkAccountVerified(trimmedEmail);

        if (isGoogleAccount) {
          return { status: 'google_account' };
        }

        if (!userExists) {
          showWarningSnackbar('User does not exist');
          return { status: 'user_not_found' };
        }

        if (isUnconfirmed) {
          await Auth.resendSignUp(trimmedEmail);
          navigate('/confirmsignup', { state: { email: trimmedEmail, status: 'unverified' } });
          return { status: 'navigated' };
        }

        await Auth.forgotPassword(trimmedEmail);
        localStorage.setItem('startTime', String(new Date()));
        navigate('/newPassword', { state: { email: trimmedEmail } });
        return { status: 'navigated' };
      } catch (error: unknown) {
        const err = error as { name?: string; code?: string; message?: string };

        if (err.name === 'LimitExceededException') {
          throw new ForgotPasswordFieldError(err.message ?? 'Too many attempts');
        }

        if (err.code === 'UserNotFoundException') {
          try {
            const response = await axios.post(API_ENDPOINTS.REACT_APP_SIGNUP_CHECK, { email: trimmedEmail });
            showWarningSnackbar(response.data.error);
          } catch {
            showWarningSnackbar('Network Error !');
          }
          return { status: 'user_not_found' };
        }

        throw new ForgotPasswordFieldError(
          "Email doesn't exist. if you signed up using Google please login with Google button"
        );
      }
    },
    false,
    { rethrow: true }
  );

  const forceLogout = useAsync(async ({ accessToken, email, password }: ForceLogoutAndLoginParams) => {
    await globalSignOut(accessToken);
    await logoutCognitoSessions();
    await Auth.signIn(email, password);

    const deviceKey = Date.now().toString();
    localStorage.setItem('deviceKey', deviceKey);
    await completeLogin(deviceKey);
    setShowActiveSessionModal(false);
  }, false);

  const loading = login.loading || googleSignIn.loading || forgotPassword.loading || forceLogout.loading;

  const dismissActiveSessionModal = useCallback(() => {
    setShowActiveSessionModal(false);
  }, []);

  const dismissActiveSessionAndSignOut = useCallback(async () => {
    setShowActiveSessionModal(false);
    await Auth.signOut();
    localStorage.clear();
    await persistor.purge();
  }, []);

  const confirmActiveSessionLogin = useCallback(
    async (values: LoginFormValues) => {
      const accessToken = await getCurrentAccessToken();
      if (!accessToken) {
        return;
      }
      setShowActiveSessionModal(false);
      await forceLogout.run({ accessToken, email: values.email, password: values.password });
    },
    [forceLogout]
  );

  return {
    loading,
    showActiveSessionModal,
    login: login.run,
    googleSignIn: googleSignIn.run,
    forgotPassword: forgotPassword.run,
    confirmActiveSessionLogin,
    dismissActiveSessionModal,
    dismissActiveSessionAndSignOut,
  };
}
